import { useState } from "react";
import { createAxios } from "../../common/axios";
import { CustomerDto, CustomerListItemDto } from "./types";
import { useDialog } from "../../common/dialog";
import { DuplicateCustomersDialog } from "./DuplicateCustomersDialog";
import { createEntityStore } from "../../common/entityStore";
import { toNullIfEmpty } from "../../common/localization";

interface Store {
    newCustomer?: CustomerDto;
    duplicateCustomers: CustomerListItemDto[];
    duplicityType: "matchCode" | "address" | undefined;
    inCheck: boolean;
    leavingPage: boolean;
}

const initialState: Store = {
    duplicateCustomers: [], duplicityType: undefined,
    inCheck: false, leavingPage: false,
};

export function useCustomerDuplicityCheck(saveCustomer: () => void) {
    const [useStore] = useState(() => createEntityStore<Store>({ data: initialState }));
    const store = useStore();
    const duplicateCustomersDialog = useDialog(close => <DuplicateCustomersDialog close={close}
        saveCustomer={saveCustomer}
        newCustomer={store.clientData.newCustomer!}
        setLeavingPage={() => store.setClientDataProp("leavingPage", true)}
        duplicityType={store.clientData.duplicityType!}
        duplicateCustomers={store.clientData.duplicateCustomers} />, "xl");

    function clear() {
        useStore.getState().setClientData(initialState);
    }

    async function check(entity: Partial<CustomerDto>, entityId: string | number | null | undefined) {
        if (useStore.getState().clientData.inCheck || toNullIfEmpty(entityId) != null) {
            return true;
        }
        clear();
        const { data: duplicateCustomersByMatchCode } = await createAxios().post<CustomerListItemDto[]>("customers/customers/getCustomersByMatchCode", entity);
        if (duplicateCustomersByMatchCode.length > 0) {
            useStore.getState().setClientDataProp("inCheck", true);
            useStore.getState().setClientDataProp("newCustomer", entity as CustomerDto);
            useStore.getState().setClientDataProp("duplicityType", "matchCode");
            useStore.getState().setClientDataProp("duplicateCustomers", duplicateCustomersByMatchCode);
            duplicateCustomersDialog.show(() => {
                clear();
            });
            return false;
        }
        const { data: duplicateCustomersByAddress } = await createAxios().post<CustomerListItemDto[]>("customers/customers/getCustomersByAddress", entity);
        if (duplicateCustomersByAddress.length > 0) {
            useStore.getState().setClientDataProp("inCheck", true);
            useStore.getState().setClientDataProp("newCustomer", entity as CustomerDto);
            useStore.getState().setClientDataProp("duplicityType", "address");
            useStore.getState().setClientDataProp("duplicateCustomers", duplicateCustomersByAddress);
            duplicateCustomersDialog.show(() => {
                clear();
            });
            return false;
        }
        return true;
    }

    return { check, render: duplicateCustomersDialog.render, getLeavingPage: () => useStore.getState().clientData.leavingPage };
}